import { Badge, Stack, Text, useMantineTheme } from "@mantine/core";
import dayjs from "dayjs";
import { cloneElement, ReactElement } from "react";

import { useSession } from "@/hooks/@custom/useSession";
import { QueueByCompany, useGetSettingsByCompanyQuery } from "@/store/apis/nextcargo";

import { ExportBtn } from "../ExportBtn";
import { ExpeditionTabRowV2, ExpeditionTabRowV2Props } from "./ExpeditionTabRowV2";
import { useExpeditionTabContextV2 } from "./ExpeditionTabV2.hooks";

type TabExpeditionContentV2Props = {
  row: {
    render: (
      row: QueueByCompany["result"]["original"][number],
      index: number,
      arr: QueueByCompany["result"]["original"]
    ) => ReactElement<ExpeditionTabRowV2Props, typeof ExpeditionTabRowV2>;
    getKey?: (row: QueueByCompany["result"]["original"][number], index: number) => React.Key;
  };
};
export function ExpeditionTabContentV2({ row }: TabExpeditionContentV2Props) {
  const { expedition } = useSession();
  const companyId = Number(expedition?.company.id);

  const { queues } = useExpeditionTabContextV2();
  const { data } = useGetSettingsByCompanyQuery({ id: companyId }, { skip: !companyId });
  const groupedQueues = queues?.grouped || [];

  const { colors } = useMantineTheme();
  const blue = colors.blue[6];

  return (
    <Stack>
      {groupedQueues.map(({ date, queues, length }, i) => {
        const formattedDate = date.replaceAll("/", "-");

        if (length === 0) return null;

        return (
          <section key={i}>
            <div className="flex items-center justify-between border-b-4" id={`table-${formattedDate}`} style={{ borderColor: blue }}>
              <ExportBtn>
                <ExportBtn.PDF
                  data={queues}
                  columnsV2={[
                    { key: "position", headerName: "Posição", getValue: (row) => `${row.position}°` },
                    { key: "truck", headerName: "Placa", getValue: (row) => row.truck.licensePlate },
                    {
                      key: "date",
                      headerName: "Atendimento",
                      getValue: (row) => dayjs(row.date).format("HH:mm:ss")
                    },
                    {
                      key: "CUSTOM",
                      headerName: "Tolerância",
                      getValue: (row) => {
                        const toleranceTime = dayjs(data?.tolerance, "HH:mm");
                        const date = dayjs(row.date)
                          .add(toleranceTime.diff(dayjs("00:00", "HH:mm")), "milliseconds")
                          .format("HH:mm:ss");

                        return date;
                      }
                    }
                  ]}
                  title={`Expedição ${date}`}
                />

                <ExportBtn.CSV
                  data={queues}
                  columnsV2={[
                    { key: "position", headerName: "Posição", getValue: (row) => `${row.position}°` },
                    { key: "truck", headerName: "Placa", getValue: (row) => row.truck.licensePlate },
                    { key: "date", headerName: "Atendimento", getValue: (row) => dayjs(row.date).format("HH:mm:ss") },
                    {
                      key: "CUSTOM",
                      headerName: "Tolerância",
                      getValue: (row) => {
                        const toleranceTime = dayjs(data?.tolerance, "HH:mm");
                        const date = dayjs(row.date)
                          .add(toleranceTime.diff(dayjs("00:00", "HH:mm")), "milliseconds")
                          .format("HH:mm:ss");
                        return date;
                      }
                    }
                  ]}
                  title={`Expedição ${date}`}
                />
              </ExportBtn>

              <div className="flex items-center">
                <Badge size="lg" color="blue" mr="sm">
                  Total: {length}
                </Badge>

                <div className="rounded-tl-sm rounded-tr-sm p-1 md:px-3 md:py-1.5" style={{ background: blue }}>
                  <Text c="white" size="lg" fw="bold" data-value={date} id={`table-head-${formattedDate}`}>
                    {date}
                  </Text>
                </div>
              </div>
            </div>

            <Stack gap="0" className="rounded-b-lg bg-blue-50 pb-1 md:pb-2">
              {queues.map((queue, index, arr) => {
                const element = row.render(queue, index, arr);
                const key = row.getKey ? row.getKey(queue, index) : index;

                return cloneElement(element, { key });
              })}
            </Stack>
          </section>
        );
      })}
    </Stack>
  );
}
